import { serviceRoleClient } from './serviceRoleClient';

const CALLS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min
const NAME_CACHE_TTL_MS = 30 * 60 * 1000; // 30 min
const NAME_LOOKUP_BATCH = 200; // phones per RPC call
const NAME_LOOKUP_MAX_PHONES = 500; // hard cap per enrich pass

/** @type {Map<string, { at: number, rows: any[] }>} */
const callsCache = new Map();
/** @type {Map<string, { at: number, name: string }>} */
const nameCache = new Map();
/** @type {Map<string, { at: number, user: any }>} */
const tcCache = new Map();

const localDayStartIso = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
};

const localDayEndIso = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
};

export const effectiveDurationSeconds = (row) => {
  const talk = Number(row?.talk_duration_seconds);
  if (Number.isFinite(talk) && talk > 0) return talk;
  const agent = Number(row?.agent_on_call_duration_seconds);
  return Number.isFinite(agent) && agent > 0 ? agent : 0;
};

const last10 = (v) => {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d;
};

const callsCacheKey = (fromDate, toDate) => `${fromDate}|${toDate}`;

const pruneExpired = (map, ttl) => {
  const now = Date.now();
  for (const [k, v] of map.entries()) {
    if (!v || now - v.at > ttl) map.delete(k);
  }
};

const attachTelecallers = async (rows) => {
  const now = Date.now();
  const missing = [];
  const tcIds = Array.from(new Set(rows.map((r) => r.telecaller_id).filter(Boolean)));

  for (const id of tcIds) {
    const hit = tcCache.get(id);
    if (hit && now - hit.at < NAME_CACHE_TTL_MS) continue;
    missing.push(id);
  }

  for (let i = 0; i < missing.length; i += 100) {
    const chunk = missing.slice(i, i + 100);
    const { data: users, error: uErr } = await serviceRoleClient
      .from('users')
      .select('id, name, phone, email')
      .in('id', chunk);
    if (!uErr && Array.isArray(users)) {
      users.forEach((u) => tcCache.set(u.id, { at: Date.now(), user: u }));
    }
  }

  return rows.map((r) => {
    const tc = r.telecaller_id ? tcCache.get(r.telecaller_id)?.user : null;
    return {
      ...r,
      full_row: r,
      effective_seconds: effectiveDurationSeconds(r),
      telecaller_name: tc?.name || null,
      telecaller_phone: tc?.phone || null,
      customer_name: r.customer_name || null,
    };
  });
};

/** RPC-only name lookup for a limited phone set. Never throws. Never uses giant ilike OR. */
const lookupCustomerNamesByPhones = async (phones) => {
  const map = new Map();
  const unique = Array.from(
    new Set((phones || []).map((p) => last10(p)).filter((p) => p && p.length >= 10)),
  ).slice(0, NAME_LOOKUP_MAX_PHONES);
  if (!unique.length) return map;

  const now = Date.now();
  const missing = [];
  for (const p of unique) {
    const hit = nameCache.get(p);
    if (hit && now - hit.at < NAME_CACHE_TTL_MS) {
      if (hit.name) map.set(p, hit.name);
    } else {
      missing.push(p);
    }
  }
  if (!missing.length) return map;

  try {
    for (let i = 0; i < missing.length; i += NAME_LOOKUP_BATCH) {
      const chunk = missing.slice(i, i + NAME_LOOKUP_BATCH);
      const { data, error } = await serviceRoleClient.rpc('admin_customer_names_by_phones', {
        p_phones: chunk,
      });
      if (error) throw error;
      (data || []).forEach((row) => {
        const key = last10(row.phone10);
        const n = String(row.customer_name || '').trim();
        if (!key || !n) return;
        map.set(key, n);
        nameCache.set(key, { at: Date.now(), name: n });
      });
      chunk.forEach((p) => {
        if (!nameCache.has(p)) nameCache.set(p, { at: Date.now(), name: '' });
      });
    }
  } catch (e) {
    // Do NOT fall back to giant PostgREST ilike — that hung Load for minutes / 400s.
    console.warn('Customer name RPC skipped:', e?.message || e);
  }

  return map;
};

export const FaultDetectorService = {
  clearCallsCache() {
    callsCache.clear();
  },

  /** Update cached rows for a date range (e.g. after name enrich) without DB hit */
  putCallsCache(fromDate, toDate, rows) {
    if (!fromDate || !toDate || !Array.isArray(rows)) return;
    const existing = callsCache.get(callsCacheKey(fromDate, toDate));
    callsCache.set(callsCacheKey(fromDate, toDate), {
      at: existing?.at || Date.now(),
      rows,
    });
  },

  getCacheInfo(fromDate, toDate) {
    pruneExpired(callsCache, CALLS_CACHE_TTL_MS);
    const hit = callsCache.get(callsCacheKey(fromDate, toDate));
    if (!hit) return null;
    return { cached: true, ageMs: Date.now() - hit.at, ttlMs: CALLS_CACHE_TTL_MS };
  },

  /**
   * Fast path: call rows + TC names only. Customer names loaded separately via enrichCustomerNames.
   * @param {{ fromDate: string, toDate: string, force?: boolean }} params
   */
  async listCallsInRange({ fromDate, toDate, force = false }) {
    const fromIso = localDayStartIso(fromDate);
    const toIso = localDayEndIso(toDate);
    if (!fromIso || !toIso) throw new Error('Please choose a valid date range');

    const key = callsCacheKey(fromDate, toDate);
    pruneExpired(callsCache, CALLS_CACHE_TTL_MS);

    if (!force) {
      const hit = callsCache.get(key);
      if (hit && Date.now() - hit.at < CALLS_CACHE_TTL_MS) {
        return { rows: hit.rows, fromCache: true, ageMs: Date.now() - hit.at };
      }
    }

    const pageSize = 1000;
    let from = 0;
    const all = [];

    for (;;) {
      const { data, error } = await serviceRoleClient.rpc('admin_fault_detector_calls', {
        p_from: fromIso,
        p_to: toIso,
        p_offset: from,
        p_limit: pageSize,
      });
      if (error) {
        const msg = String(error.message || '');
        if (/could not find the function|schema cache|does not exist/i.test(msg)) {
          throw new Error(
            'Fault Detector RPC missing. Run supabase/security-fault-detector-staff.sql in Supabase, then log out and log in.',
          );
        }
        if (/admin login required|not allowed|permission denied/i.test(msg)) {
          throw new Error('Admin session required. Log out, log in again, then Force refresh.');
        }
        throw error;
      }
      const parsed = data && typeof data === 'object' && !Array.isArray(data) ? data : { rows: data };
      const batch = Array.isArray(parsed.rows) ? parsed.rows : [];
      all.push(...batch);
      if (!parsed.has_more || batch.length < pageSize) break;
      from += pageSize;
    }

    const rows = await attachTelecallers(all);
    callsCache.set(key, { at: Date.now(), rows });
    return { rows, fromCache: false, ageMs: 0 };
  },

  /**
   * Enrich a subset of rows with customer_name (RPC + cache). Safe to call after UI is shown.
   * @param {any[]} rows
   * @param {string[]=} preferPhones — look these up first (e.g. long/repeat phones)
   */
  async enrichCustomerNames(rows, preferPhones = []) {
    if (!Array.isArray(rows) || !rows.length) return rows;

    const prefer = Array.from(
      new Set((preferPhones || []).map(last10).filter((p) => p && p.length >= 10)),
    );
    const rest = [];
    const seen = new Set(prefer);
    for (const r of rows) {
      const p = last10(r.customer_number10);
      if (!p || seen.has(p)) continue;
      seen.add(p);
      rest.push(p);
    }
    const phones = [...prefer, ...rest].slice(0, NAME_LOOKUP_MAX_PHONES);
    const nameMap = await lookupCustomerNamesByPhones(phones);
    if (!nameMap.size) return rows;

    return rows.map((r) => {
      const p = last10(r.customer_number10);
      const n = p ? nameMap.get(p) : null;
      if (!n) return r;
      return { ...r, customer_name: r.customer_name || n };
    });
  },

  async listLongCalls({ fromDate, toDate, minSeconds, force = false }) {
    const { rows } = await this.listCallsInRange({ fromDate, toDate, force });
    const minSecs = Math.max(0, Number(minSeconds) || 0);
    return rows.filter((r) => r.effective_seconds >= minSecs);
  },
};
